import "./style.css";
import { extractFrames } from "./extractFrames";
import type { QualityPreset, TrackDataset } from "./format";
import type { Incoming, Outgoing } from "./tracker/worker";
import { TrackScene } from "./viewer/TrackScene";

const viewport = must("#viewport");
const dropzone = must("#dropzone");
const fileInput = must<HTMLInputElement>("#file");
const sampleBtn = must<HTMLButtonElement>("#sample-btn");
const qualitySel = must<HTMLSelectElement>("#quality");
const videoEl = must<HTMLVideoElement>("#video");
const videoEmpty = must("#video-empty");
const phaseEl = must("#phase");
const percentEl = must("#percent");
const barFill = must("#bar-fill");
const playBtn = must<HTMLButtonElement>("#play-btn");
const scrub = must<HTMLInputElement>("#scrub");
const frameLabel = must("#frame-label");
const exportBtn = must<HTMLButtonElement>("#export-btn");
const trailsToggle = must<HTMLInputElement>("#trails");
const videoPlaneToggle = must<HTMLInputElement>("#video-plane");
const hud = must("#hud");
const statTracks = must("#stat-tracks");
const statFrames = must("#stat-frames");
const statBackend = must("#stat-backend");
const statDynamic = must("#stat-dynamic");

const scene = new TrackScene(viewport);
const worker = new Worker(new URL("./tracker/worker.ts", import.meta.url), { type: "module" });

let dataset: TrackDataset | null = null;
let playing = false;
let objectUrl: string | null = null;
let busy = false;

const resize = () => scene.resize();
window.addEventListener("resize", resize);
new ResizeObserver(resize).observe(viewport);

dropzone.addEventListener("dragover", (e) => {
  e.preventDefault();
  dropzone.classList.add("drag");
});
dropzone.addEventListener("dragleave", () => dropzone.classList.remove("drag"));
dropzone.addEventListener("drop", (e) => {
  e.preventDefault();
  dropzone.classList.remove("drag");
  const file = e.dataTransfer?.files[0];
  if (file) void runFile(file);
});
fileInput.addEventListener("change", () => {
  const file = fileInput.files?.[0];
  if (file) void runFile(file);
});
sampleBtn.addEventListener("click", () => void loadSample());

playBtn.addEventListener("click", () => togglePlay());
scrub.addEventListener("input", () => {
  if (!dataset) return;
  const frame = Number(scrub.value);
  seekToFrame(frame);
});
trailsToggle.addEventListener("change", () => scene.setShowTrails(trailsToggle.checked));
videoPlaneToggle.addEventListener("change", () => scene.setShowVideo(videoPlaneToggle.checked));
exportBtn.addEventListener("click", () => {
  if (!dataset) return;
  const blob = new Blob([JSON.stringify(dataset)], { type: "application/json" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "tracks.json";
  a.click();
});

videoEl.addEventListener("timeupdate", syncFromVideo);
videoEl.addEventListener("seeked", syncFromVideo);
videoEl.addEventListener("play", () => {
  playing = true;
  playBtn.textContent = "Pause";
});
videoEl.addEventListener("pause", () => {
  playing = false;
  playBtn.textContent = "Play";
});

window.addEventListener("keydown", (e) => {
  if (e.code === "Space" && dataset && !isTyping(e.target)) {
    e.preventDefault();
    togglePlay();
  }
});

worker.addEventListener("message", (event: MessageEvent<Outgoing>) => {
  const msg = event.data;
  if (msg.type === "progress") {
    setProgress(msg.phase, msg.ratio);
    return;
  }
  if (msg.type === "error") {
    busy = false;
    setProgress(`Error: ${msg.message}`, 0);
    hud.textContent = msg.message;
    return;
  }
  busy = false;
  dataset = msg.dataset;
  applyDataset(msg.dataset);
});

async function loadSample(): Promise<void> {
  const res = await fetch("./sample.mp4");
  if (!res.ok) {
    setProgress("Sample clip missing — run npm run sample", 0);
    return;
  }
  const blob = await res.blob();
  await runFile(new File([blob], "sample.mp4", { type: blob.type || "video/mp4" }));
}

async function runFile(file: File): Promise<void> {
  if (busy) return;
  busy = true;
  dataset = null;
  playBtn.disabled = true;
  scrub.disabled = true;
  exportBtn.disabled = true;
  setProgress("Extracting frames", 0.02);
  hud.textContent = "extracting…";

  try {
    const quality = qualitySel.value as QualityPreset;
    const extracted = await extractFrames(file, quality, (ratio, phase) => setProgress(phase, ratio * 0.2));
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    objectUrl = extracted.objectUrl;
    videoEl.src = objectUrl;
    videoEmpty.classList.add("hidden");
    await videoEl.play().catch(() => undefined);
    videoEl.pause();
    videoEl.currentTime = 0;

    const incoming: Incoming = {
      type: "run",
      backend: "lk-pyramid",
      quality,
      pack: extracted.pack,
    };
    worker.postMessage(incoming, [extracted.pack.rgba]);
    setProgress("Tracking in worker", 0.22);
  } catch (err) {
    busy = false;
    const message = err instanceof Error ? err.message : String(err);
    setProgress(`Error: ${message}`, 0);
    hud.textContent = message;
  }
}

function applyDataset(data: TrackDataset): void {
  scene.setDataset(data, videoEl);
  const max = Math.max(0, data.frameCount - 1);
  scrub.max = String(max);
  scrub.value = "0";
  scrub.disabled = max === 0;
  playBtn.disabled = data.frameCount === 0;
  exportBtn.disabled = data.tracks.length === 0;
  const dyn = data.tracks.filter((t) => t.dynamic > 0.35).length;
  statTracks.textContent = String(data.tracks.length);
  statFrames.textContent = String(data.frameCount);
  statBackend.textContent = data.backend;
  statDynamic.textContent = String(dyn);
  frameLabel.textContent = `0 / ${max}`;
  hud.textContent = `${data.tracks.length} tracks · ${data.backend} · three r186`;
  setProgress("Ready", 1);
  seekToFrame(0);
  if (data.tracks.length > 0) {
    void videoEl.play();
  }
}

function seekToFrame(frame: number): void {
  if (!dataset) return;
  scene.setFrame(frame);
  frameLabel.textContent = `${frame} / ${Math.max(0, dataset.frameCount - 1)}`;
  const t = dataset.frameCount <= 1 ? 0 : (frame / (dataset.frameCount - 1)) * dataset.durationSec;
  if (Math.abs(videoEl.currentTime - t) > 1 / Math.max(dataset.fps, 1)) {
    videoEl.currentTime = t;
  }
}

function syncFromVideo(): void {
  if (!dataset || dataset.frameCount === 0) return;
  const t = videoEl.currentTime;
  const frame = Math.round((t / Math.max(dataset.durationSec, 1e-3)) * (dataset.frameCount - 1));
  const clamped = Math.max(0, Math.min(dataset.frameCount - 1, frame));
  if (clamped !== scene.currentFrame) {
    scene.setFrame(clamped);
    scrub.value = String(clamped);
    frameLabel.textContent = `${clamped} / ${dataset.frameCount - 1}`;
  }
}

function togglePlay(): void {
  if (!dataset) return;
  if (playing) videoEl.pause();
  else void videoEl.play();
}

function setProgress(phase: string, ratio: number): void {
  phaseEl.textContent = phase;
  const pct = Math.round(Math.max(0, Math.min(1, ratio)) * 100);
  percentEl.textContent = `${pct}%`;
  barFill.style.width = `${pct}%`;
}

function must<T extends HTMLElement = HTMLElement>(sel: string): T {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing ${sel}`);
  return el;
}

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement;
}
